import { resolveCursorZone } from './cursor-zone.js';
import { computeScrollDepthPercent } from './scroll-depth.js';
import { patchPageBehaviorState } from './behavior-state.js';
import { setLastPointerPosition } from './pointer-state.js';

export type TrackCursorBehaviorOptions = {
  throttleMs?: number;
};

/**
 * Throttled pointer + scroll tracking for page behavior context (phase 5.6).
 */
export function trackCursorBehavior(
  options?: TrackCursorBehaviorOptions,
): () => void {
  if (typeof window === 'undefined') return () => {};

  const throttleMs = options?.throttleMs ?? 200;
  let lastSampleAt = 0;

  const sample = (clientY?: number) => {
    const now = Date.now();
    if (now - lastSampleAt < throttleMs) return;
    lastSampleAt = now;

    const y =
      clientY ??
      (typeof window !== 'undefined' ? window.innerHeight / 2 : 0);

    patchPageBehaviorState({
      cursorZone: resolveCursorZone(y, window.innerHeight),
      maxScrollDepthPercent: computeScrollDepthPercent(),
    });
  };

  const onMouseMove = (event: MouseEvent) => {
    setLastPointerPosition(event.clientX, event.clientY);
    sample(event.clientY);
  };
  const onScroll = () => sample();
  const onResize = () => sample();

  window.addEventListener('mousemove', onMouseMove, { passive: true });
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });

  sample();

  return () => {
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onResize);
  };
}
